import { useCallback, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import useMediaQuery from '@/hooks/useMediaQuery';
import { desktop } from '@/constants/screensizes';
import Button from '@/components/atoms/button';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import Spinner from '../../spinner';
import QuantityWidget from '@/components/atoms/quantity-widget';
import { LineItem } from '@shared/types/cart';
import MobileCartItem from './mobile-cart-item';
import { PreferredDeliveryDate, RemoveButton, AddNote, CartInSufficientStockText } from './cart-options';
import Typography from '@/components/atoms/typography';
import Modal from '@/components/organisms/modal';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import useFormat from '@/hooks/useFormat';
import { Currency } from '@/types/currency';
import { getGstMultiplier } from '@/utils/currency-helpers';

const ItemHeader = ({ label, className }: { label: string; className?: string }) => {
  return (
    <div className={`itemheader ${className}`}>
      <span>{label}</span>
    </div>
  );
};

const LoadSpinner = () => {
  return (
    <>
      <Spinner />
      <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
    </>
  );
};

const CartLineItem = ({
  lineItem,
  onUpdateQuantity,
  onRemove,
  updateLoadingStatus,
  vouchers,
}: {
  lineItem: LineItem;
  onUpdateQuantity: Function;
  onRemove: Function;
  updateLoadingStatus: Function;
  vouchers: any[];
}) => {
  const productType = lineItem?.isBrochure
  const { variant, name, productId, _url, price, count = 0, totalPrice, bundleProducts = [], quantityInCart} = lineItem;
  const { formatCurrency } = useFormat();
  const [primaryImg] = variant?.images ?? [];
  const colour = variant?.attributes?.colour;
  const availableQuantity = variant?.availableQuantity || 0;
const [quantity, setQuantity] = useState(quantityInCart ?? count);

useEffect(() => {
  setQuantity(quantityInCart ?? count);
}, [quantityInCart, count]);
  const sku = variant?.sku;
  const variantKey = productId;

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const body = document.body;
    if (isModalOpen) {
      body.style.overflow = 'hidden';
      body.classList.add('modal-open');
    } else {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    }
    return () => {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const quantityChangeHandler = async (value: number) => {
  if (value > availableQuantity) {
    return;
  }

  if (value > 500) {
    return;
  }

  updateLoadingStatus(true);

  const freeItemsCount = vouchers?.[0]?.freeItemsCount ?? 0;

  setQuantity(value + freeItemsCount);

  if (value === 0) {
    await onRemove(sku);
  } else {
    await onUpdateQuantity(sku, value, variantKey);
  }

  setTimeout(() => updateLoadingStatus(false));
};

  const onAvailableLimtExceeds = (rawValue: number) => {
    const isModalOpen = rawValue > availableQuantity;

    if (isModalOpen) {
      setIsModalOpen(isModalOpen);
    }
  };

  const removeHandler = async () => {
    updateLoadingStatus(true);
    await onRemove(sku);
    setTimeout(() => updateLoadingStatus(false));
  };

  return (
    <div className="relative grid grid-cols-[1fr_1fr_1.5fr_0.75fr_1fr_1fr_1.5fr] gap-2  border-b border-[#D1D1D1] py-8 ">
      <div className=" lg:h-[100px] lg:w-[100px] xl:h-[140px] xl:w-[140px]">
        <Link link={_url ?? '#'}>
          {primaryImg ? (
            <Image className="h-full w-full" alt={name as string} src={primaryImg} />
          ) : (
            <Image src="/images/No_product_Image.jpg" className="h-full w-full" alt="empty product image" />
          )}
        </Link>
      </div>

      <div className="mb-[40px] flex  justify-center text-[20px] leading-6 ">
        <Link link={_url ?? '#'}>
          {!productType && (
            <Typography className="mb-[40px] mt-[28px] flex justify-center text-[20px] font-normal leading-[24.2px]">
              {productId}
            </Typography>
          )}
        </Link>
      </div>
      <div className="mb-[40px] mt-[24px] flex flex-col  gap-1.5  text-[20px] leading-6 ">
        <Link link={_url ?? '#'}>
          <Typography className="!mb-6 pl-[50px] text-[20px] font-semibold leading-[24.2px] !underline-offset-4 ">{name}</Typography>
        </Link>
        {bundleProducts?.length > 0 &&
          bundleProducts?.map((bundleProduct) => {
            return (
              <Link link={_url ?? '#'}>
                {' '}
                <Typography className="pl-[50px] text-[17px] leading-[18.6px]">{bundleProduct.name}</Typography>{' '}
              </Link>
            );
          })}
      </div>
      <div className="mb-[40px] mt-[28px] flex justify-center text-[20px] leading-6 ">
        <Typography className="text-[20px] font-normal leading-[24.2px]">{colour}</Typography>
      </div>
      <div className="mb-[40px] mt-[28px] flex justify-center text-[20px] leading-6">
        <Typography className="text-[20px] font-normal leading-[24.2px]">
          {formatCurrency(((price?.centAmount as number) * getGstMultiplier(price?.currencyCode)) / 100, price?.currencyCode as Currency)}
        </Typography>
      </div>
      <div className="mditems-start mb-[40px] ml-2 mt-0 mt-[28px] flex flex-col  justify-center text-[20px] leading-6 md:mt-[24px] md:items-center md:justify-start">
        <QuantityWidget
          onChange={quantityChangeHandler}
          value={quantity}
          minValue={1}
          maxValue={500}
          showLabel={false}
        />
        {quantity >= 200 && quantity < 500 && (
          <span className="mt-3 block text-xs font-normal leading-4 text-[#E32C53]">
            High quantity
          </span>
        )}
        {(productType ? quantity >= 40 : quantity >= 500) && (
          <span className="mt-3 block text-xs font-normal leading-4 text-[#E32C53]">
            Max quantity
          </span>
        )}
      </div>
      <div className="mb-[40px] mt-[22px] flex justify-center text-[20px] leading-6  leading-8">
        <Typography className="text-[35px] font-semibold leading-[42.36px]">
          {formatCurrency(((totalPrice?.centAmount as number) * getGstMultiplier(totalPrice?.currencyCode)) / 100, totalPrice?.currencyCode as Currency)}
        </Typography>
      </div>
      <div className="absolute bottom-[28px] right-[28px] flex items-center gap-4">
        {/* <PreferredDeliveryDate /> */}
        {/* <AddNote /> */}
        <RemoveButton className="" handler={removeHandler} />
      </div>

      <div>
        <Modal
          className="flex flex-col items-center justify-center !p-10"
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          preventScroll={false}
          closeButton={true}
          size="md"
          variant="default"
          centered={true}
        >
          <>
            <Typography className="mt-2 px-4 text-center text-4xl !font-bold leading-[54.9px] text-black">
              Insufficient Stock
            </Typography>
            <Typography className="mt-10 px-1 text-center text-[20px] !font-medium leading-[27.1px] text-black">
              Unfortunately, we do not have the stock available for your selected quantity. Please reduce the number in the cart or check again later when new stock is available.
            </Typography>
            <div className="flex gap-8 pb-6">
              <Button
                size="custom"
                variant="secondary"
                type="submit"
                onClick={closeModal}
                className="mt-10 bg-[#2B2C39] !py-3 px-6 text-white "
              >
                Back To Cart
              </Button>
            </div>
          </>
        </Modal>
      </div>
    </div>
  );
};

const CartBody = (props: any) => {
  const { formatCurrency } = useFormat();
  const { lineItems, onUpdateQuantity, onRemove, sum, clearCart, onCheckout, onAddCoupon, onRemoveCoupon, vouchers } = props;
  
  console.log('===========lineItems====================',lineItems);
  console.log('===============vouchers================',vouchers);
  console.log('===============================');
  
  
  const totalDiscount = (vouchers ?? []).reduce((acc: number, v: any) => acc + (v?.discount?.amount ?? 0), 0);
  const [loading, setLoading] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const router = useRouter();
  const [isDesktop] = useMediaQuery(desktop);
  const cartContainsItems = lineItems?.length > 0;
  const handleContinueShoppingButtonClick = () => {
    router.push('/');
  };

  const updateLoadingStatus = useCallback((status: boolean) => {
    setLoading(status);
  }, []);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError('');
    const alreadyApplied = (vouchers ?? []).some(
      (v: any) => v?.code?.toLowerCase() === couponCode.trim().toLowerCase(),
    );
    const result = await onAddCoupon?.(couponCode.trim());
    if (result?.data?.errorMessage?.trim()) {
      setCouponError(result.data.errorMessage);
    }
    //if (result?.data?.errorMessage === 'InvalidDiscountCode') {
    //  setCouponError('Invalid Coupon Code');
    //}
  };

  return (
    <div className="w-full">
      {!lineItems && <Spinner />}
      {isDesktop && (
        <div className="mt-4 flex flex-col bg-white px-[48px] md:px-[16px]">
          {cartContainsItems && (
            <>
              {loading && <LoadSpinner />}
              <div className="grid grid-cols-[1fr_1fr_1.5fr_0.75fr_1fr_1fr_1.5fr] place-items-center gap-2 whitespace-nowrap border-b border-[#D1D1D1] py-4 text-[24.29px] font-medium leading-[29.4px] ">
                <ItemHeader className="w-full place-items-start" label="Item" />
                <ItemHeader label="Code" />
                <ItemHeader label="Description" />
                <ItemHeader label="Colour" />
                <ItemHeader
                  label="Price Per Unit"
                  className="lg:mt-[30px]   xl:mt-0  lg:[&>span]:whitespace-normal xl:[&>span]:whitespace-nowrap"
                />
                <ItemHeader label="Quantity" />
                <ItemHeader label="Price" />
              </div>
              <div className="">
                {lineItems?.length > 0 &&
                  lineItems.map((lineItem: LineItem) => {
                    return (
                      <CartLineItem
  key={lineItem.lineItemId}
  updateLoadingStatus={updateLoadingStatus}
  onRemove={onRemove}
  onUpdateQuantity={onUpdateQuantity}
  lineItem={lineItem}
  vouchers={vouchers}
/>
                    );
                  })}
              </div>
              {totalDiscount === 0 && (
                <div className="flex flex-col border-b border-[#D1D1D1] px-[16px] py-[28px]">
                  <div className="flex w-full items-center gap-3">
                    <label className="whitespace-nowrap text-[18px] font-medium leading-5">Coupon Code</label>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                      placeholder="Coupon code"
                      className="h-[48px] flex-1 border border-[#D1D1D1] px-4 text-[16px] focus:outline-none focus:border-[#2B2C39]"
                    />
                    <Button
                      size="custom"
                      variant="secondary"
                      onClick={handleApplyCoupon}
                      className="h-[48px] cursor-pointer whitespace-nowrap !px-[24px] text-[18px] font-normal leading-5"
                    >
                      Apply
                    </Button>
                  </div>
                  {couponError && (
                    <span className="mt-2 text-[15px] text-[#E32C53]">{couponError}</span>
                  )}
                </div>
              )}
              {totalDiscount > 0 && (vouchers ?? []).map((v: any, i: number) => (
                <div key={i} className="flex w-full items-center justify-end gap-[24px] px-[16px] pb-[8px] pt-[28px]">
                  {v.freeItemsCount > 0 ? (
                    <span className="text-[20px] font-medium text-[#E32C53]">
                      Discount (+{v.freeItemsCount} free)
                    </span>
                  ) : (
                    <span className="text-[20px] font-medium text-[#E32C53]">
                      Discount (-{v.discount.displayAmount})
                    </span>
                  )}
                  <button
                    onClick={() => onRemoveCoupon?.(v.code)}
                    className="cursor-pointer text-[16px] font-normal text-[#2B2C39] underline hover:opacity-70"
                  >
                    Remove Discount
                  </button>
                </div>
              ))}
              <div className="flex w-full items-start justify-end gap-[48px] px-[16px] py-[28px]">
                <div className="flex flex-col  items-end">
                  <span className='text-[29px] font-bold' >Total Price</span>
                  <span className='text-[15.5px]' >(inc. GST)</span>
                </div>
                {sum?.currencyCode && (
                  <div className="text-[35px] font-semibold leading-[42.36px]">
                    <Typography className="text-[35px] font-semibold leading-[42.36px]">
                      {formatCurrency(((sum?.centAmount as number) * getGstMultiplier(sum?.currencyCode)) / 100, sum?.currencyCode)}
                    </Typography>
                  </div>
                )}
              </div>
              <div className="mb-[48px] flex w-full items-center justify-between">
                <div className="flex max-w-[264px] items-center  gap-4 bg-white">
                  <Link link={"/"}>
                    <Button
                    size="full"
                    Icon={ChevronLeftIcon}
                    iconClassName='w-[32px] h-[32px]'
                    iconPosition="left"
                    className="h-[48px] !px-[24px] !py-[8px] !cursor-pointer text-[18px] font-normal leading-5"
                    variant="secondary"
                  >
                    Continue Shopping
                  </Button>
                  </Link>
                </div>
                <div className="flex w-2/5 items-center gap-4">
                  <Button
                    size="full"
                    onClick={clearCart}
                    className="h-[48px] !cursor-pointer text-[18px] font-normal leading-5"
                    variant="secondary"
                  >
                    Clear Cart
                  </Button>
                  <Button
                    size="full"
                    onClick={onCheckout}
                    className="h-[48px] !cursor-pointer  !bg-black text-[18px] font-normal leading-5"
                    variant="primary"
                  >
                    Checkout
                  </Button>
                </div>
              </div>
            </>
          )}
          {!cartContainsItems && !!lineItems && (
            <div className="flex w-full cursor-default flex-wrap justify-center gap-1.5 py-[300px] text-22 [&>a]:cursor-pointer  [&>a]:!underline ">
              <Typography as="p" className="pt-1 !text-[22px] font-normal leading-6">
                {'Your cart is empty... click'}
              </Typography>
              <Link className='' link={'/'}>here</Link>
              <Typography as="p" className="pt-1 !text-[22px] font-normal leading-6">
                {'to continue shopping'}
              </Typography>
            </div>
          )}
        </div>
      )}
      {!isDesktop && (
        <div className="mt-4 flex flex-col bg-white px-[16px] md:px-[16px] ">
          {lineItems?.length > 0 &&
            lineItems.map((lineItem: LineItem, index: number) => {
              return (
                <MobileCartItem
                  showFooter={index === lineItems.length - 1}
                  key={lineItem.lineItemId}
                  sum={sum}
                  onRemove={onRemove}
                  onUpdateQuantity={onUpdateQuantity}
                  lineItem={lineItem}
                  clearCart={clearCart}
                  onCheckout={onCheckout}
                  onAddCoupon={onAddCoupon}
                  onRemoveCoupon={onRemoveCoupon}
                  vouchers={vouchers}
                />
              );
            })}
        </div>
      )}
      {!cartContainsItems && !!lineItems && !isDesktop && (
        <div className="flex   w-full cursor-default flex-wrap justify-center gap-1.5 py-[200px] text-22 [&>a]:cursor-pointer [&>a]:!underline ">
          <Typography as="p" className="pt-1 !text-[22px]">
            {'Your cart is empty... click'}
          </Typography>
          <Link link={'/'}>here</Link>
          <Typography as="p" className="pt-1 !text-[22px]">
            {'to continue shopping'}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default CartBody;
